import React, { useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { Icon, Div, Text } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';
import useModels from 'src/hooks/swr/useModels';
import { CellTag } from 'src/components/models/cells';
import Button from 'src/components/button';
import BaseDialog from './base';
/* eslint-disable react/prop-types */

const getDownloadDialogTitle = (tag) => {
  if ([CONSTANTS.SEGMENT.TAG.VALIDATION, CONSTANTS.SEGMENT.TAG.ACTUARIAL, 'ACTVAL'].includes(tag)) {
    return 'Download validation predictions';
  }

  if (tag === CONSTANTS.SEGMENT.TAG.TARGET) {
    return 'Download predictions';
  }

  return 'Downloads';
};

export function DownloadDialog({ isOpen, actions, downloads = [], tag }) {
  const modelsQuery = useMemo(() => {
    const query = {
      filters: [
        { name: 'recent_status', op: 'in', val: [CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS] },
        { name: 'data_model_type', op: 'neq', val: CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL },
      ],
      order_by: [
        {
          field: 'name',
          direction: 'desc',
        },
      ],
      fields: 'items{id,name,dataModelType,predictionType,typeDetail},*',
    };
    return query;
  }, []);

  // query models to be able to match downloads against name for backwards compatibility when downloads does not contain dataModelType field
  const { data: modelList = [] } = useModels({
    query: modelsQuery,
    // @ts-ignore
    config: { revalidateOnMount: true, shouldFetch: downloads.length && downloads.some((d) => !d.dataModelType) },
  });

  // name check for backwards compatability of old downloads
  // can likely be removed in the future
  const modelTypes = downloads.reduce((a, download) => {
    if (modelList.length) {
      modelList.forEach((model) => {
        if (download.name === model.name) {
          // eslint-disable-next-line no-param-reassign
          a[download.id] = {
            dataModelType: model?.dataModelType,
            predictionType: model?.predictionType,
            typeDetail: model?.typeDetail,
          };
        }
      });
    }
    return a;
  }, {});

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });
  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{ close: actions.close, cancel: actions.cancel }}
      title={{
        text: getDownloadDialogTitle(tag),
        backgroundColor: 'brand300',
        color: 'white',
        textSize: 'title',
      }}
      items={{
        content: [
          ...downloads.map(({ name, id, dataModelType }, idx) => (
            <Div
              key={id}
              d="flex"
              flexDir="row"
              justify="space-between"
              align="center"
              m={{ t: idx !== 0 ? '1.25rem' : '0' }}
            >
              {(modelTypes?.[id] || dataModelType) && (
                <CellTag value={modelTypes[id] || { dataModelType }} bg="white" m={{ r: '.75rem' }} />
              )}
              <Text textSize="subtitle" textColor="tabSelected">
                {name.replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[modelTypes[id]?.dataModelType || dataModelType]}:`, '')}
              </Text>
              <Div flexGrow="1" />
              <Button
                buttonStyle="primary"
                w="1.875rem"
                onClick={() => actions.handlers[1].on(id, name)}
                data-tip={`Delete "${name}"`}
              >
                <Icon color="white" name="Delete" viewBox="0 0 50 50" size=".875rem" cursor="pointer" />
              </Button>
              <Button
                buttonStyle="primary"
                m={{ r: '0rem' }}
                data-test={`Download "${name}"`}
                onClick={() => actions.handlers[0].on(id)}
              >
                <Icon
                  color="white"
                  name="Download2"
                  viewBox="0 0 14 14"
                  size=".875rem"
                  cursor="pointer"
                  m={{ r: '1rem' }}
                />
                <Text textWeight="500">DOWNLOAD</Text>
              </Button>
            </Div>
          )),
          <Div key="last-spacer" data-test="last-spacer" h="1.875rem" m={{ t: '1.25rem' }} />,
        ],
      }}
    />
  );
}

/* eslint-disable react/require-default-props */
DownloadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
  items: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.element).isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        textSize: PropTypes.string,
        close: PropTypes.bool.isRequired,
      }).isRequired
    ),
  }).isRequired,
  downloads: PropTypes.arrayOf(PropTypes.shape({})),
  tag: PropTypes.string.isRequired,
};
/* eslint-enable */

export default DownloadDialog;
